import { useState } from 'react';
import { useParams } from 'react-router-dom';
import {
    useRoles,
    useUserRoles,
    useAssignRolesToUser,
    useAddUserToProject,
    useRemoveUserRoles,
} from '@/services/project-role.service';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Trash2Icon, UserPlusIcon, UsersIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Section from '../../components/Section';

type ScanorhizeError = {
    errorCode?: string;
    message?: string;
    statusCode?: number;
};

export default function RoleList() {
    const { projectId } = useParams();
    const { data: roles = [], error: rolesError } = useRoles(projectId);
    const { data: userRoles = [], error: userRolesError } =
        useUserRoles(projectId);
    const { mutate: assignRole, error: assignRoleError } =
        useAssignRolesToUser();
    const { mutate: addUser, error: addUserError } = useAddUserToProject();
    const { mutate: removeUser, error: removeUserError } = useRemoveUserRoles();
    const [newUserEmail, setNewUserEmail] = useState('');
    const [newUserRoleId, setNewUserRoleId] = useState('');
    const { t } = useTranslation();

    if (
        addUserError &&
        (addUserError as ScanorhizeError).errorCode !== 'user.entity_not_found'
    ) {
        return (
            <Section title={t('Collaborators')}>
                <p className="text-sm text-red-600">
                    {t('Error...:')} {addUserError?.message}
                </p>
            </Section>
        );
    }
    if (rolesError || userRolesError || assignRoleError || removeUserError) {
        return (
            <Section title={t('Collaborators')}>
                <p className="text-sm text-red-600">
                    {t('Error...:')}{' '}
                    {rolesError?.message ||
                        userRolesError?.message ||
                        assignRoleError?.message ||
                        removeUserError?.message}
                </p>
            </Section>
        );
    }
    if (!projectId) {
        return (
            <Section title={t('Collaborators')}>
                <p className="text-sm text-red-600">
                    {t('Project ID is missing')}
                </p>
            </Section>
        );
    }

    return (
        <Section title={t('Collaborators')}>
            {userRoles.length === 0 ? (
                <div className="text-center py-8 text-[#7a8077]">
                    <UsersIcon className="h-8 w-8 mx-auto mb-2 text-[#cdd0c7]" />
                    <p className="text-sm">{t('No team members yet')}</p>
                </div>
            ) : (
                <ul className="divide-y divide-[#ecede8]">
                    {userRoles.map((userRole) => (
                        <li
                            key={userRole.userId}
                            className="flex items-center justify-between py-2.5"
                        >
                            <div className="flex items-center gap-3 min-w-0">
                                <div className="h-7 w-7 rounded-full bg-[#e8efe9] text-[#2d6a4f] flex items-center justify-center text-xs font-semibold flex-shrink-0">
                                    {userRole.user.firstName?.[0]}
                                    {userRole.user.lastName?.[0]}
                                </div>
                                <span className="text-sm font-medium text-[#1c2620] truncate">
                                    {userRole.user.firstName}{' '}
                                    {userRole.user.lastName}
                                </span>
                                {/* Hidden from the project's users; flagged
                                    for us. */}
                                {userRole.user.isSuperAdmin && (
                                    <span className="shrink-0 rounded bg-[#f0f2ee] px-1.5 py-0.5 text-[11px] text-[#5a6259]">
                                        Humeos
                                    </span>
                                )}
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                                <Select
                                    value={userRole.roleId}
                                    onValueChange={(value: string) =>
                                        assignRole({
                                            projectId,
                                            userId: userRole.userId,
                                            roleId: value,
                                        })
                                    }
                                >
                                    <SelectTrigger className="w-32 h-8 text-xs">
                                        <SelectValue placeholder={t('Role')} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {roles.map((role) => (
                                            <SelectItem
                                                key={role.id}
                                                value={role.id}
                                            >
                                                {role.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() =>
                                        removeUser({
                                            projectId,
                                            userId: userRole.userId,
                                        })
                                    }
                                    className="h-7 w-7 text-[#7a8077] hover:text-red-600 hover:bg-red-50"
                                    title={t('Remove user')}
                                >
                                    <Trash2Icon className="h-3.5 w-3.5" />
                                </Button>
                            </div>
                        </li>
                    ))}
                </ul>
            )}

            <div className="border-t border-[#ecede8] mt-4 pt-4">
                <div className="flex gap-2">
                    <Input
                        type="email"
                        placeholder={t('User email')}
                        value={newUserEmail}
                        onChange={(e) => setNewUserEmail(e.target.value)}
                        className="flex-1 h-9 text-sm"
                    />
                    <Select
                        value={newUserRoleId}
                        onValueChange={setNewUserRoleId}
                    >
                        <SelectTrigger className="w-32 h-9 text-xs">
                            <SelectValue placeholder={t('Role')} />
                        </SelectTrigger>
                        <SelectContent>
                            {roles.map((role) => (
                                <SelectItem key={role.id} value={role.id}>
                                    {role.name}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <Button
                        onClick={() =>
                            addUser({
                                projectId,
                                userEmail: newUserEmail,
                                roleId: newUserRoleId,
                            })
                        }
                        disabled={!newUserEmail || !newUserRoleId}
                        className="h-9 bg-[#2d6a4f] hover:bg-[#245741] text-white"
                    >
                        <UserPlusIcon className="h-3.5 w-3.5 mr-1" />
                        {t('Add')}
                    </Button>
                </div>
                {addUserError &&
                    (addUserError as ScanorhizeError).errorCode ===
                        'user.entity_not_found' && (
                        <p className="text-red-600 text-xs mt-2">
                            {t('The user does not exist')}
                        </p>
                    )}
            </div>
        </Section>
    );
}
